import React, { useState, useEffect } from 'react';
import AdminService from '../services/adminService';
import { dashboardSnapshotService } from '../services/dashboardSnapshotService';
import '../css/admin/DashboardOverview.css';
import { Modal, Tooltip, Progress, Table, Tag, Tabs, Empty, Avatar, DatePicker, List } from 'antd';
import moment from 'moment';
import { InfoCircleOutlined, UserOutlined, ShopOutlined, CarOutlined, HistoryOutlined } from '@ant-design/icons';
import {
  MdRefresh,
  MdCurrencyRupee,
  MdShoppingBag,
  MdStorefront,
  MdGroup,
  MdTrendingUp,
  MdDeliveryDining,
  MdCheckCircle,
  MdTaskAlt
} from 'react-icons/md';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, Legend
} from 'recharts';
import OrdersBreakdownChart from './charts/OrdersBreakdownChart';
import CustomerMetricsChart from './charts/CustomerMetricsChart';
import TopBusinessesRevenueChart from './charts/TopBusinessesRevenueChart';
import TopBusinessesOrdersChart from './charts/TopBusinessesOrdersChart';
import DeliveryPartnersChart from './charts/DeliveryPartnersChart';

// MetricInfoIcon component for professional tooltips
const MetricInfoIcon = ({ title, content }) => (
  <Tooltip title={<div style={{ maxWidth: 250 }}><strong>{title}</strong><br />{content}</div>} placement="topRight">
    <InfoCircleOutlined style={{ color: '#1890ff', marginLeft: 6, cursor: 'help' }} />
  </Tooltip>
);

const DashboardOverview = () => {
  const [dashboardData, setDashboardData] = useState({
    platformSummary: {},
    recentOrders: [],
    businessStats: {},
    deliveryStats: {},
    summaryData: {
      revenueMetrics: {},
      orderMetrics: {},
      deliveryPartners: [],
      orderStatusBreakdown: []
    }
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [activeCustomerTab, setActiveCustomerTab] = useState('unique');
  const [alerts, setAlerts] = useState([]);
  const [alertDetails, setAlertDetails] = useState([]);
  const [startDate, setStartDate] = useState(() => {
    // Set default to 1st of current month
    return moment().startOf('month');
  });
  const [endDate, setEndDate] = useState(() => {
    // Set default to today
    return moment();
  });

  const handleAlertClick = (alert) => {
    const title = (alert.title || '').toLowerCase();
    let section = '';

    if (title.includes('pending') || title.includes('sla delayed')) {
      section = 'orders';
    } else if (title.includes('new merchant') || title.includes('new business')) {
      section = 'business-review';
    } else if (title.includes('new delivery partner')) {
      section = 'delivery-partner-review';
    }

    if (section) {
      window.location.hash = section;
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Prepare date parameters for API calls
      const dateParams = {};
      if (startDate) {
        dateParams.date_from = startDate.format('YYYY-MM-DD');
      }
      if (endDate) {
        dateParams.date_to = endDate.format('YYYY-MM-DD');
      }




      // Ensure we stay on dashboard section
      if (window.location.hash !== '#dashboard' && window.location.hash !== '') {
        window.location.hash = 'dashboard';
      }

      // FIRST: Trigger snapshot calculation
      try {
        const calculateResult = await dashboardSnapshotService.calculateSnapshot();
        if (!calculateResult.success) {
          console.warn('Snapshot calculation warning:', calculateResult.message);
        }
      } catch (calcError) {
        console.warn('Snapshot calculation error:', calcError);
      }

      // Fetch alerts
      try {
        const queryParams = new URLSearchParams(dateParams).toString();
        const alertsResponse = await AdminService.adminApiCall(`dashboard/alerts/${queryParams ? `?${queryParams}` : ''}`);

        if (alertsResponse.success && alertsResponse.data) {
          const alertCategories = alertsResponse.data;

          // Filter details based on date range
          const filterByDate = (details) => {
            if (!details) return [];
            return details.filter(d => {
              if (!d.created_at) return true;
              const date = moment(d.created_at);
              if (startDate && date.isBefore(startDate, 'day')) return false;
              if (endDate && date.isAfter(endDate, 'day')) return false;
              return true;
            });
          };

          const pendingDetails = filterByDate(alertCategories.pending_orders?.details);
          const slaDetails = filterByDate(alertCategories.sla_delayed_orders?.details);
          const merchantDetails = filterByDate(alertCategories.new_merchants?.details);
          const partnerDetails = filterByDate(alertCategories.new_delivery_partners?.details);

          const formattedAlerts = [];

          if (pendingDetails.length > 0) {
            formattedAlerts.push({
              title: 'Pending Orders',
              message: `${pendingDetails.length} orders are currently pending.`,
              severity: 'medium'
            });
          }
          if (slaDetails.length > 0) {
            formattedAlerts.push({
              title: 'SLA Delayed Orders',
              message: `${slaDetails.length} orders are delayed past their SLA.`,
              severity: 'high'
            });
          }
          if (merchantDetails.length > 0) {
            formattedAlerts.push({
              title: 'New Merchants',
              message: `${merchantDetails.length} new merchants registered.`,
              severity: 'low'
            });
          }
          if (partnerDetails.length > 0) {
            formattedAlerts.push({
              title: 'New Delivery Partners',
              message: `${partnerDetails.length} new delivery partners pending approval.`,
              severity: 'low'
            });
          }

          setAlerts(formattedAlerts);

          let allDetails = [
            ...pendingDetails.map(d => ({ ...d, type: 'Pending' })),
            ...slaDetails.map(d => ({ ...d, type: 'SLA Delayed' })),
            ...merchantDetails.map(d => ({ ...d, type: 'New Merchant' })),
            ...partnerDetails.map(d => ({ ...d, type: 'New Partner' }))
          ];

          // Sort details by created_at descending
          allDetails.sort((a, b) => {
            const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
            const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
            return dateB - dateA;
          });

          setAlertDetails(allDetails);
        }
      } catch (alertError) {
        console.warn('Failed to fetch alerts:', alertError);
      }

      // Add a small delay to ensure the new snapshot is available
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Add timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 5000)
      );

      // Call the NEW lightweight endpoint with timeout
      const data = await Promise.race([
        AdminService.getDashboardSnapshot(),
        timeoutPromise
      ]);



      if (data.success) {
        const transformedData = {
          platformSummary: {
            //totalRevenue: data.data?.revenue?.total_revenue || 0,
            totalOrders: data.data?.orders?.total_orders || 0,
            activeBusinesses: data.data?.businesses?.active_businesses || 0,
            uniqueCustomers: data.data?.customers?.unique_customers || 0,
            recentCustomers: data.data?.customers?.recent_customers_details?.length || 0,
            averageOrderValue: data.data?.revenue?.average_order_value || 0,
            activeDeliveryPartners: data.data?.delivery?.active_delivery_partners || 0
          },
          recentOrders: [],
          businessStats: {
            totalBusinessCount: data.data?.businesses?.total_businesses || 0,
            activeBusinessCount: data.data?.businesses?.active_businesses || 0,
            nonVerifiedCount: (data.data?.businesses?.total_businesses || 0) - (data.data?.businesses?.paid_businesses || 0),
            paymentPendingCount: 0
          },
          deliveryStats: {
            activePartners: data.data?.delivery?.available_drivers || 0,
            ordersInTransit: data.data?.orders?.active_orders || 0,
            completedToday: data.data?.orders?.completed_orders || 0,
            busyPartners: data.data?.delivery?.busy_drivers || 0
          },
          revenueByBusiness: [],
          ordersByBusiness: [],
          deliveryPartners: [],
          userAnalytics: {
            active_users: data.data?.customers?.active_users ?? 0,
            inactive_users: (data.data?.customers?.total_users ?? 0) - (data.data?.customers?.active_users ?? 0),
            total_users: data.data?.customers?.total_users ?? 0,
            unique_customers_details: [],
            recent_customers_details: []
          },
          statusBreakdown: [],
          allBusinesses: [],
          ordersData: data.data?.orders || {},
          businessesData: data.data?.businesses || {},
          customersData: data.data?.customers || {}
        };

        setDashboardData(transformedData);

        // Also fetch the full summary to get the detailed user analytics (logged in users, etc.)
        AdminService.getDashboardSummary(dateParams).then(summary => {
          if (summary.success && summary.user_analytics) {
            setDashboardData(prev => {
              return {
                ...prev,
                customersData: {
                  ...prev.customersData,
                  logged_in_count: summary.user_analytics.monthly_logged_in_count,
                  logged_out_count: summary.user_analytics.monthly_logged_out_count,
                  logged_in_users: summary.user_analytics.monthly_logged_in_users,
                  logged_out_users: summary.user_analytics.monthly_logged_out_users
                },
                livePlatformData: {
                  ...prev.livePlatformData,
                  todays_logged_in_count: summary.user_analytics.todays_logged_in_count || 0,
                  todays_logged_out_count: summary.user_analytics.todays_logged_out_count || 0,
                  todays_logged_in_users: summary.user_analytics.todays_logged_in_users || [],
                  todays_logged_out_users: summary.user_analytics.todays_logged_out_users || []
                },
                statusBreakdown: summary.debug_info?.status_breakdown || prev.statusBreakdown
              };
            });
          }
        }).catch(err => console.warn('Failed to fetch detailed user analytics', err));

        // Set the last updated time to the current time when refresh is clicked
        // This ensures the displayed time reflects when the user actually refreshed
        const currentTime = new Date();
        setLastUpdated(currentTime);

        // Also fetch detailed data from summary endpoint
        try {
          const summaryData = await AdminService.getDashboardSummary(dateParams);

          if (summaryData.success) {
            setDashboardData(prev => ({
              ...prev,
              recentOrders: summaryData.recent_orders || [],
              revenueByBusiness: summaryData.revenue_metrics?.business_revenue_list || [],
              ordersByBusiness: summaryData.order_metrics?.business_order_breakdown || [],
              deliveryPartners: summaryData.delivery_partner_metrics?.partners_list || [],
              userAnalytics: {
                ...prev.userAnalytics,
                active_users: summaryData.user_analytics?.active_users ?? prev.userAnalytics.active_users ?? 0,
                inactive_users: summaryData.user_analytics?.inactive_users ?? prev.userAnalytics.inactive_users ?? 0,
                total_users: summaryData.user_analytics?.total_users ?? prev.userAnalytics.total_users ?? 0,
                overall_active_users: summaryData.user_analytics?.overall_active_users ?? 0,
                overall_inactive_users: summaryData.user_analytics?.overall_inactive_users ?? 0,
                overall_total_users: summaryData.user_analytics?.overall_total_users ?? 0,
                unique_customers_details: summaryData.user_analytics?.unique_customers_details || [],
                recent_customers_details: summaryData.user_analytics?.recent_customers_details || []
              },
              platformSummary: {
                ...prev.platformSummary,
                totalRevenue: summaryData.kpi_metrics?.total_revenue ?? prev.platformSummary.totalRevenue,
                totalOrders: summaryData.kpi_metrics?.total_orders ?? prev.platformSummary.totalOrders,
                activeBusinesses: summaryData.kpi_metrics?.active_businesses ?? prev.platformSummary.activeBusinesses,
                uniqueCustomers: summaryData.kpi_metrics?.unique_customers ?? prev.platformSummary.uniqueCustomers,
                averageOrderValue: summaryData.kpi_metrics?.average_order_value ?? prev.platformSummary.averageOrderValue,
                activeDeliveryPartners: summaryData.kpi_metrics?.active_delivery_partners ?? prev.platformSummary.activeDeliveryPartners,
                recentCustomers: summaryData.user_analytics?.recent_customers_details?.length || 0
              },
              statusBreakdown: summaryData.debug_info?.status_breakdown || [],
              allBusinesses: summaryData.business_list_metrics?.all_businesses || [],
              summaryData: {
                revenueMetrics: summaryData.revenue_metrics || {},
                orderMetrics: summaryData.order_metrics || {},
                deliveryPartners: summaryData.delivery_partner_metrics?.partners_list || [],
                orderStatusBreakdown: summaryData.debug_info?.status_breakdown || [],
                recentOrders: summaryData.recent_orders || [],
                businessListMetrics: summaryData.business_list_metrics || {}
              }
            }));
          }
        } catch (summaryError) {
          console.warn('Failed to fetch summary data:', summaryError);
        }
      } else {
        throw new Error(data.message || 'API returned unsuccessful response');
      }
    } catch (err) {
      console.error('Dashboard error:', err);
      setError('Failed to load dashboard data. Please try again.');
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  };

  const consolidateBusinessData = (businessData) => {
    // Define master businesses that should be excluded from the chart
    const masterBusinesses = ['FUSION STREET', 'FUSION STREAT', 'FUSION STREAT '];

    // Define sub-business display names with their master business indication
    const subBusinessDisplayNames = {
      'DILLI GALLI': 'DILLI GALLI (Fusion Street)',
      // Add more sub-business display names here
      // 'SUB_BUSINESS_NAME': 'SUB_BUSINESS_NAME (Master Business Name)',
    };

    // Filter out master businesses and update display names for sub-businesses
    const filteredData = businessData
      .filter(business => {
        const businessName = business.business_name;
        const isMasterBusiness = masterBusinesses.includes(businessName);
        return !isMasterBusiness;
      })
      .map(business => {
        const businessName = business.business_name;
        const displayName = subBusinessDisplayNames[businessName] || businessName;

        return {
          ...business,
          business_name: displayName
        };
      });

    const result = filteredData.sort((a, b) => b.total_orders - a.total_orders);

    return result;
  };

  const openModal = (type) => {
    setModalType(type);
    setModalVisible(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    const colors = {
      pending: '#ff9800',
      confirmed: '#2196f3',
      preparing: '#ff5722',
      ready: '#4caf50',
      delivered: '#8bc34a',
      cancelled: '#f44336',
      completed: '#4caf50',
      'in progress': '#2196f3',
      'on the way': '#ff9800'
    };
    return colors[statusLower] || '#666';
  };

  const renderModalContent = () => {
    switch (modalType) {
      case 'REVENUE':
        return (
          <div className="modal-content">
            <h3 className="modal-section-title">Revenue Distribution by Business</h3>
            <div className="total-revenue-summary">
              <span>Total Revenue: </span>
              <strong>{formatCurrency(dashboardData.revenueByBusiness?.reduce((acc, cur) => acc + (cur.raw_revenue || cur.revenue || 0), 0) || 0)}</strong>
            </div>
            <List
              dataSource={dashboardData.revenueByBusiness || []}
              locale={{ emptyText: <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>No revenue data available</p> }}
              renderItem={(biz, index) => (
                <List.Item className="business-card" key={index}>
                  <div className="modal-row-header">
                    <span>{biz.business_name}</span>
                    <strong>{formatCurrency(biz.raw_revenue || biz.revenue || 0)}</strong>
                  </div>
                  <Progress
                    percent={dashboardData.platformSummary.totalRevenue > 0 ? ((biz.raw_revenue || biz.revenue || 0) / dashboardData.platformSummary.totalRevenue) * 100 : 0}
                    strokeColor="#52c41a"
                    showInfo={false}
                  />
                </List.Item>
              )}
              className="revenue-summary-cards"
              pagination={false}
            />
            <div className="modal-footer-stats">
              <div className="stat-mini"><span>Platform AOV:</span> <strong>{formatCurrency(dashboardData.platformSummary.averageOrderValue || 0)}</strong></div>
              <div className="stat-mini"><span>Top Earner of this month:</span> <strong>{dashboardData.revenueByBusiness?.[0]?.business_name || 'N/A'}</strong></div>
            </div>
          </div>
        );
      case 'ORDERS':
        return (
          <Tabs defaultActiveKey="1" items={[
            {
              key: '1',
              label: 'Status Breakdown',
              children: (
                <div style={{ textAlign: 'center' }}>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={dashboardData.statusBreakdown?.map((item, index) => ({
                          name: item.status || item.name,
                          value: item.count || item.value,
                          color: getStatusColor(item.status || item.name),
                          key: index
                        })) || []}
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {dashboardData.statusBreakdown?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getStatusColor(entry.status || entry.name)} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )
            },
            {
              key: '2',
              label: 'Recent Logs',
              children: (
                <Table
                  size="small"
                  pagination={false}
                  dataSource={dashboardData.recentOrders?.slice(0, 10).map((order, index) => ({ ...order, key: index })) || []}
                  columns={[
                    { title: 'Order ID', dataIndex: 'order_id', render: (id) => `#${id}` },
                    { title: 'Business', dataIndex: 'business_name' },
                    { title: 'Status', dataIndex: 'status', render: s => <Tag color={getStatusColor(s)}>{s}</Tag> }
                  ]}
                />
              )
            }
          ]} />
        );
      case 'DELIVERED_ORDERS':
        return (
          <div className="modal-content" style={{ padding: '24px' }}>
            <h3 className="modal-section-title">Delivered Orders (Recent)</h3>
            <Table
              size="small"
              pagination={false}
              dataSource={dashboardData.recentOrders?.filter(o => o.status?.toLowerCase() === 'delivered').slice(0, 10).map((order, index) => ({ ...order, key: index })) || []}
              columns={[
                { title: 'Order ID', dataIndex: 'order_id', render: (id) => `#${id}` },
                { title: 'Business', dataIndex: 'business_name' },
                { title: 'Status', dataIndex: 'status', render: s => <Tag color={getStatusColor(s)}>{s}</Tag> }
              ]}
            />
          </div>
        );
      case 'COMPLETED_ORDERS':
        return (
          <div className="modal-content" style={{ padding: '24px' }}>
            <h3 className="modal-section-title">Completed Orders (Recent)</h3>
            <Table
              size="small"
              pagination={false}
              dataSource={dashboardData.recentOrders?.filter(o => o.status?.toLowerCase() === 'completed').slice(0, 10).map((order, index) => ({ ...order, key: index })) || []}
              columns={[
                { title: 'Order ID', dataIndex: 'order_id', render: (id) => `#${id}` },
                { title: 'Business', dataIndex: 'business_name' },
                { title: 'Status', dataIndex: 'status', render: s => <Tag color={getStatusColor(s)}>{s}</Tag> }
              ]}
            />
          </div>
        );
      case 'CUSTOMERS':
        return (
          <Tabs defaultActiveKey="1" items={[
            {
              key: '1',
              label: 'Live Customer Activity',
              children: (
                <Table
                  size="small"
                  dataSource={(dashboardData.userAnalytics?.unique_customers_details || []).map((customer, index) => {
                    const loggedInUser = dashboardData.customersData?.logged_in_users?.find(u => u.user_id === customer.user_id);
                    const loggedOutUser = dashboardData.customersData?.logged_out_users?.find(u => u.user_id === customer.user_id);

                    let action = 'Inactive';
                    let timestamp = null;
                    if (loggedInUser) {
                      action = 'Logged In';
                      timestamp = loggedInUser.joined_date;
                    } else if (loggedOutUser) {
                      action = 'Logged Out';
                      timestamp = loggedOutUser.joined_date;
                    }

                    return {
                      ...customer,
                      key: index,
                      action,
                      timestamp
                    };
                  })}
                  pagination={{ pageSize: 10 }}
                  columns={[
                    { title: 'Name', dataIndex: 'name', render: (text) => <><UserOutlined style={{ marginRight: 8 }} />{text}</> },
                    { title: 'Email', dataIndex: 'email' },
                    { title: 'Last Order', dataIndex: 'last_order_date', render: (date) => date ? new Date(date).toLocaleDateString() : 'Never' },
                    { title: 'Action', dataIndex: 'action', render: (action) => <Tag color={action === 'Logged In' ? 'green' : action === 'Logged Out' ? 'default' : 'red'}>{action}</Tag> },
                    { title: 'Time Stamp', dataIndex: 'timestamp', render: (ts) => ts ? new Date(ts).toLocaleString() : '-' }
                  ]}
                />
              )
            },
            {
              key: '2',
              label: 'Recent Registrations',
              children: (
                <Table
                  size="small"
                  dataSource={dashboardData.userAnalytics?.recent_customers_details?.map((customer, index) => ({ ...customer, key: index })) || []}
                  pagination={{ pageSize: 10 }}
                  columns={[
                    { title: 'Name', dataIndex: 'name', render: (text) => <><UserOutlined style={{ marginRight: 8 }} />{text}</> },
                    { title: 'Email', dataIndex: 'email' },
                    { title: 'Registration', dataIndex: 'registration_date', render: (date) => date ? new Date(date).toLocaleDateString() : 'Unknown' }
                  ]}
                />
              )
            }
          ]} />
        );
      case 'BUSINESSES':
        return (
          <Table
            size="small"
            dataSource={dashboardData.summaryData?.businessListMetrics?.active_businesses_list?.map((business, index) => ({
              ...business,
              key: index,
            })) || []}
            pagination={{ pageSize: 10 }}
            columns={[
              { title: 'Business', dataIndex: 'business_name', render: (text) => <strong>{text}</strong> },
              { title: 'Owner', dataIndex: 'owner_name' },
              { title: 'Email', dataIndex: 'email' },
              { title: 'Mobile', dataIndex: 'mobile' },
              { title: 'Status', dataIndex: 'status', render: s => (<Tag color={s === 'Active' ? 'green' : 'red'}>{s}</Tag>) },
              { title: 'Joined', dataIndex: 'joined_date', render: (date) => date ? new Date(date).toLocaleDateString() : 'Unknown' }
            ]}
          />
        );
      case 'DELIVERY':
        return (
          <Table
            size="small"
            dataSource={dashboardData.deliveryPartners?.map((partner, index) => ({ ...partner, key: index })) || []}
            pagination={{ pageSize: 10 }}
            columns={[
              { title: 'Partner', dataIndex: 'name' },
              { title: 'Rating', dataIndex: 'average_rating', render: r => <Tag color="gold">★ {r !== null && r !== undefined && r !== '' ? r : 'N/A'}</Tag> },
              { title: 'Status', dataIndex: 'status', render: s => (<Tag color={s === 'Available' ? 'green' : s === 'Busy' ? 'orange' : 'red'}>{s}</Tag>) },
              { title: 'Completed', dataIndex: 'total_completed_orders' }
            ]}
          />
        );
      case 'ONLINE_CUSTOMERS':
        return (
          <Table
            size="small"
            dataSource={dashboardData.livePlatformData?.todays_logged_in_users?.map((user, index) => ({ ...user, key: index })) || []}
            pagination={{ pageSize: 10 }}
            columns={[
              { title: 'Name', dataIndex: 'name', render: (text) => <><UserOutlined style={{ marginRight: 8, color: '#10b981' }} />{text || 'Unknown User'}</> },
              { title: 'Email / Mobile', dataIndex: 'email', render: (email, record) => email || record.mobile },
              { title: 'Last Active', dataIndex: 'last_active', render: (date) => date ? new Date(date).toLocaleString() : 'Unknown' },
              { title: 'Status', key: 'status', render: () => <Tag color="green">Logged In</Tag> }
            ]}
          />
        );
      case 'OFFLINE_CUSTOMERS':
        return (
          <Table
            size="small"
            dataSource={dashboardData.livePlatformData?.todays_logged_out_users?.map((user, index) => ({ ...user, key: index })) || []}
            pagination={{ pageSize: 10 }}
            columns={[
              { title: 'Name', dataIndex: 'name', render: (text) => <><UserOutlined style={{ marginRight: 8, color: '#a0a0a0' }} />{text || 'Unknown User'}</> },
              { title: 'Email / Mobile', dataIndex: 'email', render: (email, record) => email || record.mobile },
              { title: 'Last Active', dataIndex: 'last_active', render: (date) => date ? new Date(date).toLocaleString() : 'Unknown' },
              { title: 'Status', key: 'status', render: () => <Tag color="default">Logged Out</Tag> }
            ]}
          />
        );
      case 'MONTHLY_ONLINE_CUSTOMERS':
        return (
          <Table
            size="small"
            dataSource={dashboardData.customersData?.logged_in_users?.map((user, index) => ({ ...user, key: index })) || []}
            pagination={{ pageSize: 10 }}
            columns={[
              { title: 'Name', dataIndex: 'name', render: (text) => <><UserOutlined style={{ marginRight: 8, color: '#10b981' }} />{text || 'Unknown User'}</> },
              { title: 'Email / Mobile', dataIndex: 'email', render: (email, record) => email || record.mobile },
              { title: 'Last Active', dataIndex: 'last_active', render: (date) => date ? new Date(date).toLocaleString() : 'Unknown' },
              { title: 'Status', key: 'status', render: () => <Tag color="green">Logged In</Tag> }
            ]}
          />
        );
      case 'MONTHLY_OFFLINE_CUSTOMERS':
        return (
          <Table
            size="small"
            dataSource={dashboardData.customersData?.logged_out_users?.map((user, index) => ({ ...user, key: index })) || []}
            pagination={{ pageSize: 10 }}
            columns={[
              { title: 'Name', dataIndex: 'name', render: (text) => <><UserOutlined style={{ marginRight: 8, color: '#a0a0a0' }} />{text || 'Unknown User'}</> },
              { title: 'Email / Mobile', dataIndex: 'email', render: (email, record) => email || record.mobile },
              { title: 'Last Active', dataIndex: 'last_active', render: (date) => date ? new Date(date).toLocaleString() : 'Unknown' },
              { title: 'Status', key: 'status', render: () => <Tag color="default">Logged Out</Tag> }
            ]}
          />
        );
      case 'FILTERED_USERS':
        return (
          <div className="modal-content" style={{ padding: '24px' }}>
            <h3 className="modal-section-title">Filtered Users Breakdown</h3>
            <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', marginTop: '20px' }}>
              <div style={{ textAlign: 'center', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '8px', padding: '20px', flex: 1 }}>
                <div style={{ fontSize: '14px', color: '#389e0d', marginBottom: '8px', fontWeight: 'bold' }}>Active Users</div>
                <div style={{ fontSize: '32px', color: '#52c41a', fontWeight: 'bold' }}>
                  {dashboardData.userAnalytics?.active_users || 0}
                </div>
              </div>
              <div style={{ textAlign: 'center', background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: '8px', padding: '20px', flex: 1 }}>
                <div style={{ fontSize: '14px', color: '#cf1322', marginBottom: '8px', fontWeight: 'bold' }}>Inactive Users</div>
                <div style={{ fontSize: '32px', color: '#f5222d', fontWeight: 'bold' }}>
                  {dashboardData.userAnalytics?.inactive_users || 0}
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: '24px', color: '#8c8c8c' }}>
              Total Users: <strong>{(dashboardData.userAnalytics?.active_users || 0) + (dashboardData.userAnalytics?.inactive_users || 0)}</strong>
            </div>
          </div>
        );
      case 'OVERALL_USERS':
        return (
          <div className="modal-content" style={{ padding: '24px' }}>
            <h3 className="modal-section-title">Overall Users Breakdown</h3>
            <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', marginTop: '20px' }}>
              <div style={{ textAlign: 'center', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '8px', padding: '20px', flex: 1 }}>
                <div style={{ fontSize: '14px', color: '#389e0d', marginBottom: '8px', fontWeight: 'bold' }}>Active Users</div>
                <div style={{ fontSize: '32px', color: '#52c41a', fontWeight: 'bold' }}>
                  {dashboardData.userAnalytics?.overall_active_users || 0}
                </div>
              </div>
              <div style={{ textAlign: 'center', background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: '8px', padding: '20px', flex: 1 }}>
                <div style={{ fontSize: '14px', color: '#cf1322', marginBottom: '8px', fontWeight: 'bold' }}>Inactive Users</div>
                <div style={{ fontSize: '32px', color: '#f5222d', fontWeight: 'bold' }}>
                  {dashboardData.userAnalytics?.overall_inactive_users || 0}
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'center', marginTop: '24px', color: '#8c8c8c' }}>
              Total Users: <strong>{dashboardData.userAnalytics?.overall_total_users || 0}</strong>
            </div>
          </div>
        );
      default:
        return <Empty />;
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner" style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #1890ff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '16px'
        }}></div>
        <p>Loading dashboard data...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <h3>Error Loading Dashboard</h3>
        <p>{error}</p>
        <button onClick={fetchDashboardData} className="retry-btn">
          <MdRefresh /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="pro-dashboard">
      <style>{`
        /* ── Base ── */
        .pro-dashboard {
          background: #f4f7fa;
          min-height: 100vh;
          padding: 24px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          box-sizing: border-box;
          overflow-x: hidden;
        }

        /* ── Header ── */
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: white;
          padding: 16px 24px;
          border-radius: 12px;
          margin-bottom: 24px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        .header h1 { margin: 0; font-size: 22px; color: #001529; font-weight: 700; }

        /* ── KPI Grid ── */
        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 20px;
          margin-bottom: 28px;
        }
        .kpi-card {
          background: white;
          padding: 20px;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(.25,.8,.25,1);
          border: 1px solid transparent;
          box-sizing: border-box;
        }
        .kpi-card:hover { transform: translateY(-4px); box-shadow: 0 12px 20px rgba(0,0,0,0.08); border-color: #1890ff; }
        .kpi-label { font-size: 13px; color: #8c8c8c; display: flex; align-items: center; gap: 8px; margin-bottom: 6px; justify-content: center; }
        .kpi-value { font-size: 26px; font-weight: 800; color: #262626; margin: 4px 0; letter-spacing: -0.5px; }
        .kpi-subtext { font-size: 12px; color: #bfbfbf; }

        /* ── Charts Section ── */
        .detailed-charts-section { margin-bottom: 28px; }
        .detailed-charts-section h3 { font-size: 18px; font-weight: 700; color: #1a1a1a; margin: 0 0 16px 0; }
        .dashboard-charts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 20px;
        }

        /* ── Main Layout ── */
        .main-layout {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr;
          gap: 14px;
          margin-top: 4px;
        }
        .panel {
          background: white;
          border-radius: 12px;
          padding: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          flex-direction: column;
          overflow: hidden;
        }
        .panel-title { font-size: 16px; font-weight: 700; margin-bottom: 20px; color: #262626; display: flex; align-items: center; gap: 8px; }
        .table-container { flex: 1; overflow-x: auto; }

        /* ── Modal ── */
        .revenue-modal .ant-modal-content { box-shadow: none !important; border: 1px solid #e8e8e8; }
        .modal-content { max-height: 70vh; overflow-y: auto;}
        .modal-section-title { font-size: 18px; font-weight: 700; color: #1a1a1a; margin: 0 0 16px 0; border-bottom: 1px solid #f0f0f0; padding-bottom: 12px; }
        .modal-data-row { margin-bottom: 16px; }
        .modal-row-header { display: flex; justify-content: space-between; align-items: center; font-size: 14px; margin-bottom: 8px; gap: 16px; }
        .modal-row-header span { font-weight: 500; color: #262626; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; }
        .modal-row-header strong { font-weight: 600; color: #1890ff; white-space: nowrap; }
        .revenue-summary-cards { max-height: 400px; overflow-y: auto; padding-right: 8px; }
        .total-revenue-summary { font-size: 16px; font-weight: 600; margin-bottom: 12px; color: #262626; display: flex; align-items: center; gap: 8px; }
        .modal-footer-stats {padding-top: 6px; border-top: 1px dashed #e8e8e8; display: flex; gap: 20px; flex-wrap: wrap; }
        .stat-mini { font-size: 12px; color: #8c8c8c; }
        .stat-mini strong { color: #262626; margin-left: 4px; }

        /* ── Tablet (≤ 768px) ── */
        @media (max-width: 768px) {
          .pro-dashboard { padding: 16px; }
          .header { padding: 12px 16px; margin-bottom: 16px; }
          .header h1 { font-size: 18px; }
          .kpi-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 20px; }
          .kpi-card { padding: 16px; }
          .kpi-value { font-size: 22px; }
          .dashboard-charts-grid { grid-template-columns: 1fr; gap: 14px; }
          .main-layout { grid-template-columns: 1fr; gap: 16px; }
          .panel { padding: 16px; }
          .activity-feed { max-height: 350px; }
        }

        /* ── Mobile (≤ 480px) ── */
        @media (max-width: 480px) {
          .pro-dashboard { padding: 12px 12px 80px 12px; }
          .header { padding: 10px 12px; border-radius: 10px; }
          .header h1 { font-size: 15px; }
          .header > div:first-child > div { font-size: 10px; }
          .refresh-label { display: none; }
          .kpi-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 16px; }
          .kpi-card { padding: 12px; border-radius: 10px; }
          .kpi-label { font-size: 11px; gap: 4px; }
          .kpi-label svg { display: none; }
          .kpi-value { font-size: 18px; }
          .kpi-subtext { font-size: 10px; }
          .detailed-charts-section h3 { font-size: 15px; margin-bottom: 12px; }
          .dashboard-charts-grid { gap: 12px; }
          .main-layout { gap: 12px; }
          .panel { padding: 12px; border-radius: 10px; }
          .panel-title { font-size: 14px; margin-bottom: 12px; }
          .table-container { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .activity-feed { max-height: 280px; }
          .ant-table { font-size: 12px; }
          .ant-table-thead > tr > th { font-size: 11px; padding: 8px 6px; }
          .ant-table-tbody > tr > td { font-size: 12px; padding: 8px 6px; }

          /* Business Performance Cards for Mobile - Show table above 599px */
          .business-performance-cards {
            display: none !important;
          }
          .business-performance-table {
            display: table !important;
          }
          .ant-table-thead {
            display: table-header-group !important;
          }
        }

        /* Desktop and larger screens - Show table, hide cards */
        @media (min-width: 600px) {
          .business-performance-cards {
            display: none !important;
          }
          .business-performance-table {
            display: table !important;
          }
          .ant-table-thead {
            display: table-header-group !important;
          }
        }

        /* Mobile Business Performance Cards */
        @media (max-width: 599px) {
          .business-performance-cards {
            display: flex !important;
            flex-direction: column;
            gap: 12px;
          }
          .business-performance-table {
            display: none !important;
          }
          .ant-table-thead {
            display: none !important;
          }
          
          .business-card {
            background: white;
            border: 1px solid #e8e8e8;
            border-radius: 10px;
            padding: 16px;
            margin-bottom: 12px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.05);
            transition: all 0.3s ease;
            cursor: pointer;
          }
          
          .business-card:hover {
            border-color: #1890ff;
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
            transform: translateY(-2px);
          }
          
          .business-card-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 12px;
          }
          
          .business-card-name {
            font-size: 14px;
            font-weight: 600;
            color: #262626;
            flex: 1;
            line-height: 1.3;
          }
          
          .business-card-revenue {
            font-size: 16px;
            font-weight: 700;
            color: #1890ff;
            white-space: nowrap;
          }
          
          .business-card-metrics {
            display: flex;
            gap: 16px;
            flex-wrap: wrap;
          }
          
          .business-card-metric {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }
          
          .business-card-metric-label {
            font-size: 11px;
            color: #8c8c8c;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .business-card-metric-value {
            font-size: 13px;
            font-weight: 600;
            color: #262626;
          }
          
          .business-card-metric-value.orders {
            color: #1890ff;
          }
          
          .business-card-metric-value.aov {
            color: #52c41a;
          }
        }

        /* Bar Chart Container Styles */
        .bar-chart-container {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          height: 320px;
          position: relative;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        
        .bar-chart-container h3 {
          font-size: 14px;
          font-weight: 700;
          margin: 0 0 16px 0;
          color: #1a1a1a;
          line-height: 1.4;
        }
        
        .bar-chart-container .recharts-wrapper {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 0;
        }
        
        .bar-chart-container .recharts-bar {
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
        }
        
        .bar-chart-container .recharts-bar-rectangle {
          transition: all 0.3s ease;
          cursor: pointer;
        }
        
        .bar-chart-container .recharts-bar-rectangle:hover {
          filter: brightness(1.1) drop-shadow(0 4px 8px rgba(0,0,0,0.15));
          transform: translateY(-2px);
        }
        
        .bar-chart-container .recharts-cartesian-grid {
          stroke-dasharray: 3, 3;
          stroke: #f5f5f5;
        }
        
        .bar-chart-container .recharts-cartesian-axis-tick {
          font-size: 11px;
          fill: #8c8c8c;
        }
        
        .bar-chart-container .recharts-cartesian-axis-line {
          stroke: #f0f0f0;
          stroke-width: 1;
        }
        
        .bar-chart-container .recharts-tooltip-wrapper {
          background: rgba(255, 255, 255, 0.95);
          border: 1px solid #e8e8e8;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          backdrop-filter: blur(10px);
        }
        
        .bar-chart-container .recharts-tooltip-content {
          font-size: 12px;
          color: #595959;
        }
        
        /* Responsive Bar Chart */
        @media (max-width: 768px) {
          .bar-chart-container {
            height: 280px;
            padding: 16px;
          }
          
          .bar-chart-container h3 {
            font-size: 13px;
            margin-bottom: 12px;
          }
          
          .bar-chart-container .recharts-cartesian-axis-tick {
            font-size: 10px;
          }
        }
        
        @media (max-width: 480px) {
          .bar-chart-container {
            height: 280px;
            padding: 12px;
          }
          
          .bar-chart-container h3 {
            font-size: 12px;
            margin-bottom: 8px;
          }
          
          .bar-chart-container .recharts-cartesian-axis-tick {
            font-size: 9px;
          }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .spin { animation: spin 1s linear infinite; }
      `}</style>

      <div className="header">
        <div>
          <h1>Dashboard Overview</h1>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <DatePicker
            value={startDate}
            onChange={(date) => {
              setStartDate(date);
              // Auto-refresh when both dates are selected
              if (date && endDate) {
                fetchDashboardData();
              }
            }}
            style={{
              padding: '6px 8px',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              background: 'white',
              fontSize: '13px',
              cursor: 'pointer',
              width: '120px',
            }}
            placeholder="Start Date"
            format="YYYY-MM-DD"
          />
          <DatePicker
            value={endDate}
            onChange={(date) => {
              setEndDate(date);
              // Auto-refresh when both dates are selected
              if (date && startDate) {
                fetchDashboardData();
              }
            }}
            style={{
              padding: '6px 8px',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              background: 'white',
              fontSize: '13px',
              cursor: 'pointer',
              width: '120px',
            }}
            placeholder="End Date"
            format="YYYY-MM-DD"
          />
          <button
            className="refresh-btn"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); fetchDashboardData(); }}
            disabled={loading}
            type="button"
            style={{
              padding: '6px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              background: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: 'black',
              gap: '6px',
              flexShrink: 0,
              fontSize: '13px',
              whiteSpace: 'nowrap',
            }}
          >
            <MdRefresh className={loading ? 'spin' : ''} />
            <span className="refresh-label">{loading ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#333', marginBottom: '12px' }}>Performance Metrics (Date Filtered)</h3>
      <div className="kpi-grid">
        <div className="kpi-card" onClick={() => openModal('REVENUE')}>
          <div className="kpi-label"><MdCurrencyRupee size={24} style={{ color: '#FF986C' }} /> Total Revenue</div>
          <div className="kpi-value">{formatCurrency(dashboardData.platformSummary.totalRevenue || 0)}</div>
          <div className="kpi-subtext">Click to view per-business revenue</div>
        </div>

        <div className="kpi-card" onClick={() => openModal('ORDERS')}>
          <div className="kpi-label"><MdShoppingBag size={24} style={{ color: '#FF986C' }} /> Total Orders</div>
          <div className="kpi-value">{(dashboardData.platformSummary.totalOrders || 0).toLocaleString()}</div>
          <div className="kpi-subtext">Platform-wide order volume</div>
        </div>

        <div className="kpi-card" onClick={() => openModal('DELIVERED_ORDERS')}>
          <div className="kpi-label"><MdCheckCircle size={24} style={{ color: '#FF986C' }} /> Delivered Orders</div>
          <div className="kpi-value">
            {dashboardData.statusBreakdown?.find(s => s.status === 'delivered')?.count || 0}
          </div>
          <div className="kpi-subtext">Successfully delivered</div>
        </div>

        <div className="kpi-card" onClick={() => openModal('COMPLETED_ORDERS')}>
          <div className="kpi-label"><MdTaskAlt size={24} style={{ color: '#FF986C' }} /> Completed Orders</div>
          <div className="kpi-value">
            {dashboardData.statusBreakdown?.find(s => s.status === 'completed')?.count || 0}
          </div>
          <div className="kpi-subtext">Total completed</div>
        </div>

        <div className="kpi-card" onClick={() => openModal('REVENUE')}>
          <div className="kpi-label"><MdTrendingUp size={24} style={{ color: '#FF986C' }} /> Average Order Value</div>
          <div className="kpi-value">{formatCurrency(dashboardData.platformSummary.averageOrderValue || 0)}</div>
          <div className="kpi-subtext">Platform average transaction value</div>
        </div>

        <div className="kpi-card" onClick={() => openModal('FILTERED_USERS')}>
          <div className="kpi-label"><MdGroup size={24} style={{ color: '#FF986C' }} /> New Users</div>
          <div className="kpi-value">{(dashboardData.userAnalytics?.active_users || 0) + (dashboardData.userAnalytics?.inactive_users || 0)}</div>
          <div className="kpi-subtext">Click to view active/inactive breakdown</div>
        </div>

        <div className="kpi-card" onClick={() => openModal('OVERALL_USERS')}>
          <div className="kpi-label"><MdGroup size={24} style={{ color: '#FF986C' }} /> Overall Total Users</div>
          <div className="kpi-value">{(dashboardData.userAnalytics?.overall_total_users || 0).toLocaleString()}</div>
          <div className="kpi-subtext">Click to view active/inactive breakdown</div>
        </div>

        <div className="kpi-card" onClick={() => openModal('MONTHLY_ONLINE_CUSTOMERS')}>
          <div className="kpi-label"><UserOutlined style={{ color: '#FF986C', fontSize: 24 }} /> Online Customers</div>
          <div className="kpi-value" style={{ color: '#10b981' }}>{dashboardData.customersData?.logged_in_count || 0}</div>
          <div className="kpi-subtext">Active users within date range</div>
        </div>

        <div className="kpi-card" onClick={() => openModal('MONTHLY_OFFLINE_CUSTOMERS')}>
          <div className="kpi-label"><UserOutlined style={{ color: '#a0a0a0', fontSize: 24 }} /> Offline Customers</div>
          <div className="kpi-value">{dashboardData.customersData?.logged_out_count || 0}</div>
          <div className="kpi-subtext">Inactive users within date range</div>
        </div>
      </div>

      <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#333', marginTop: '24px', marginBottom: '12px' }}>Live Platform Status</h3>
      <div className="kpi-grid">
        <div className="kpi-card" onClick={() => openModal('BUSINESSES')}>
          <div className="kpi-label"><MdStorefront size={24} style={{ color: '#FF986C' }} /> Active Businesses</div>
          <div className="kpi-value">{dashboardData.platformSummary.activeBusinesses || 0}</div>
          <div className="kpi-subtext">Currently operational partners</div>
        </div>

        <div className="kpi-card" onClick={() => openModal('DELIVERY')}>
          <div className="kpi-label"><MdDeliveryDining size={24} style={{ color: '#FF986C' }} /> Active Partners</div>
          <div className="kpi-value">{dashboardData.platformSummary.activeDeliveryPartners || 0}</div>
          <div className="kpi-subtext">Currently available for delivery</div>
        </div>

        <div className="kpi-card" onClick={() => openModal('ONLINE_CUSTOMERS')}>
          <div className="kpi-label"><UserOutlined style={{ color: '#FF986C', fontSize: 24 }} /> Online Customers</div>
          <div className="kpi-value" style={{ color: '#10b981' }}>{dashboardData.livePlatformData?.todays_logged_in_count || 0}</div>
          <div className="kpi-subtext">Todays logged in users</div>
        </div>

        <div className="kpi-card" onClick={() => openModal('OFFLINE_CUSTOMERS')}>
          <div className="kpi-label"><UserOutlined style={{ color: '#a0a0a0', fontSize: 24 }} /> Offline Customers</div>
          <div className="kpi-value">{dashboardData.livePlatformData?.todays_logged_out_count || 0}</div>
          <div className="kpi-subtext">Todays logged out</div>
        </div>
      </div>

      {/* Business Performance Analytics Charts */}
      <div className="detailed-charts-section">
        <h3>Business Performance Analytics</h3>
        <div className="dashboard-charts-grid">
          <TopBusinessesRevenueChart data={dashboardData.summaryData?.revenueMetrics?.business_revenue_list || []} />
          <TopBusinessesOrdersChart data={consolidateBusinessData(dashboardData.summaryData?.orderMetrics?.business_order_breakdown || [])} />
          <OrdersBreakdownChart data={dashboardData.ordersData || {}} />
          <CustomerMetricsChart data={dashboardData.customersData || {}} />
          <DeliveryPartnersChart data={dashboardData.summaryData?.deliveryPartners || []} />
          {alerts && alerts.length > 0 && (
            <div style={{ background: '#fff3cd', border: '1px solid #ffeeba', borderRadius: '8px', padding: '16px', display: 'flex', flexDirection: 'column', maxHeight: '350px' }}>
              <h3 style={{ color: '#856404', margin: '0 0 10px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <InfoCircleOutlined /> System Alerts ({alerts.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto' }}>
                {alerts.map((alert, idx) => (
                  <div
                    key={idx}
                    style={{ background: 'white', padding: '10px 12px', borderRadius: '6px', borderLeft: `4px solid ${alert.severity === 'high' ? '#dc3545' : alert.severity === 'medium' ? '#ffc107' : '#17a2b8'}`, fontSize: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', transition: 'background-color 0.2s' }}
                    onClick={() => handleAlertClick(alert)}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <div>
                      <strong style={{ display: 'block', marginBottom: '4px' }}>{alert.title || alert.type || 'Alert'}</strong>
                      <span style={{ color: '#666' }}>{alert.message || alert.description}</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#999', whiteSpace: 'nowrap' }}>
                      {alert.created_at ? new Date(alert.created_at).toLocaleString() : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>


      <div className="main-layout">
        <div className="panel">
          <div className="panel-title"><ShopOutlined /> Business Performance Breakdown</div>

          {/* Card Layout for Mobile */}
          <div className="business-performance-cards">
            {dashboardData.summaryData?.revenueMetrics?.business_revenue_list?.length > 0 ? (
              dashboardData.summaryData?.revenueMetrics?.business_revenue_list?.slice(0, 10).map((business, index) => {
                // Find corresponding order data for this business
                const orderData = dashboardData.summaryData?.orderMetrics?.business_order_breakdown?.find(o => o.business_name === business.business_name);


                return (
                  <div
                    key={index}
                    className="business-card"
                    onClick={() => { setModalType('REVENUE'); setModalVisible(true); }}
                  >
                    <div className="business-card-header">
                      <div className="business-card-name">{business.business_name}</div>
                      <div className="business-card-revenue">{formatCurrency(business.raw_revenue || 0)}</div>
                    </div>
                    <div className="business-card-metrics">
                      <div className="business-card-metric">
                        <div className="business-card-metric-label">Orders</div>
                        <div className="business-card-metric-value orders">
                          {orderData?.total_orders || business.orders || 0}
                        </div>
                      </div>
                      <div className="business-card-metric">
                        <div className="business-card-metric-label">AOV</div>
                        <div className="business-card-metric-value aov">
                          {formatCurrency(
                            (orderData?.total_orders || business.orders || 0) > 0 ?
                              (business.raw_revenue || 0) / (orderData?.total_orders || business.orders || 1) : 0
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#8c8c8c' }}>
                <ShopOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }} />
                <p style={{ margin: 0, fontSize: 14 }}>No business data available</p>
                <p style={{ margin: '8px 0 0 0', fontSize: 12 }}>Data will appear once businesses start processing orders</p>
              </div>
            )}
          </div>

          {/* Table Layout for Desktop */}
          <div className="table-container business-performance-table">
            {dashboardData.summaryData?.revenueMetrics?.business_revenue_list?.length > 0 ? (
              <Table
                dataSource={dashboardData.summaryData?.revenueMetrics?.business_revenue_list?.slice(0, 10).map((business, index) => {
                  // Find corresponding order data for this business
                  const orderData = dashboardData.summaryData?.orderMetrics?.business_order_breakdown?.find(o => o.business_name === business.business_name);

                  return {
                    key: index,
                    business_name: business.business_name,
                    revenue: business.raw_revenue || 0,
                    total_orders: orderData?.total_orders || business.orders || 0,
                    average_order_value: (orderData?.total_orders || business.orders || 0) > 0 ?
                      (business.raw_revenue || 0) / (orderData?.total_orders || business.orders || 1) : 0
                  };
                }) || []}
                pagination={false}
                size="middle"
                onRow={(record) => ({ onClick: () => { setModalType('REVENUE'); setModalVisible(true); } })}
                columns={[
                  { title: 'Business Entity', dataIndex: 'business_name', key: 'business_name', render: (t) => <strong>{t}</strong> },
                  { title: 'Revenue', dataIndex: 'revenue', key: 'revenue', render: v => formatCurrency(v || 0) },
                  { title: 'Orders', dataIndex: 'total_orders', key: 'total_orders', render: v => <Tag color="blue">{v || 0}</Tag> },
                  { title: 'AOV', dataIndex: 'average_order_value', key: 'average_order_value', render: v => formatCurrency(v || 0) }
                ]}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#8c8c8c' }}>
                <ShopOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }} />
                <p style={{ margin: 0, fontSize: 14 }}>No business data available</p>
                <p style={{ margin: '8px 0 0 0', fontSize: 12 }}>Data will appear once businesses start processing orders</p>
              </div>
            )}
          </div>
        </div>

        <div className="panel">
          <div className="panel-title"><HistoryOutlined /> Recent Live Activity</div>
          <div className="activity-feed">
            {dashboardData.recentOrders && dashboardData.recentOrders.length > 0 ? (
              dashboardData.recentOrders.slice(0, 10).map((order, idx) => (
                <div key={idx} style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>#{order.order_id}</span>
                    <span style={{ fontSize: 11, color: '#bfbfbf' }}>
                      {order.created_at ? new Date(order.created_at).toLocaleTimeString() : 'Unknown time'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, color: '#595959', flex: 1 }}>{order.business_name}</span>
                    <span style={{ fontSize: 12, color: '#595959', fontWeight: 600 }}>{formatCurrency(order.final_amount || order.total_amount || 0)}</span>
                    <Tag color={getStatusColor(order.status)} style={{ fontSize: 10, flexShrink: 0 }}>{order.status}</Tag>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#8c8c8c' }}>
                <HistoryOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }} />
                <p style={{ margin: 0, fontSize: 14 }}>No recent activity</p>
                <p style={{ margin: '8px 0 0 0', fontSize: 12 }}>Recent orders will appear here</p>
              </div>
            )}
          </div>
        </div>

        <div className="panel">
          <div className="panel-title"><InfoCircleOutlined /> Alert Details</div>
          <div className="activity-feed">
            {alertDetails && alertDetails.length > 0 ? (
              alertDetails.map((detail, idx) => (
                <div
                  key={idx}
                  style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0', borderLeft: `4px solid ${detail.type === 'SLA Delayed' ? '#dc3545' : detail.type === 'Pending' ? '#ffc107' : '#17a2b8'}`, paddingLeft: '8px' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: '#2c3e50' }}>
                      {detail.order_number || detail.business_name || detail.name || 'Alert Item'}
                      <Tag color={detail.type === 'SLA Delayed' ? 'red' : detail.type === 'Pending' ? 'orange' : 'blue'} style={{ marginLeft: 8, fontSize: 10 }}>{detail.type}</Tag>
                    </span>
                    <span style={{ fontSize: 11, color: '#bfbfbf' }}>
                      {detail.created_at ? new Date(detail.created_at).toLocaleDateString() : ''}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 12, color: '#595959', flex: 1 }}>
                      {detail.business_name && detail.order_number ? detail.business_name : ''}
                    </span>
                    {detail.total_amount && (
                      <span style={{ fontSize: 12, color: '#595959', fontWeight: 600 }}>{formatCurrency(detail.total_amount)}</span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#8c8c8c' }}>
                <InfoCircleOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }} />
                <p style={{ margin: 0, fontSize: 14 }}>No alert details this month</p>
                <p style={{ margin: '8px 0 0 0', fontSize: 12 }}>System is running smoothly</p>
              </div>
            )}
          </div>
        </div>

        {modalVisible && (
          <Modal
            visible={modalVisible}
            onCancel={() => setModalVisible(false)}
            footer={null}
            centered
            width={800}
            bodyStyle={{ padding: 0 }}
            destroyOnClose
            className="revenue-modal"
          >
            {renderModalContent()}
          </Modal>
        )}
      </div>
    </div>
  );
};

export default DashboardOverview;
